/**
 * AgreedValueDialog — Feature 5: Agreed Value Billing
 *
 * Shown when the PM clicks "Set agreed value for selected lines" in the
 * Bulk Adjustments dropdown inside the Invoice Generation Wizard (Step 1).
 *
 * The dialog lets the PM enter an agreed/discounted total for the selected
 * lines. The difference between the actual total and the agreed value is
 * calculated as a write-off. If the "Require write-off reason" setting is
 * enabled, the PM must choose a reason before confirming.
 *
 * Write-off reasons and the requireWriteOffReason flag are fetched from the
 * settings API (GET /api/settings/write-off-reasons). Falls back to
 * localStorage values if the API is not yet available.
 *
 * The actual write-off WIP entries are created on the backend when the
 * invoice is posted to Financials (PostToFinancialsAsync), not at dialog
 * confirm time.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, TrendingDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { getApiBaseUrl } from "@/lib/data-service";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AgreedValueLineItem {
  id: string;
  description: string;
  amount: number;
}

export interface AgreedValueResult {
  /** IDs of the affected line items */
  lineIds: string[];
  /** The agreed total the PM entered */
  agreedTotal: number;
  /** The write-off amount (actualTotal − agreedTotal) */
  writeOffAmount: number;
  /** Write-off reason code (if required) */
  writeOffReason?: string;
  /** Scaling factor to apply to each line item's amount (agreedTotal / actualTotal) */
  scaleFactor: number;
}

interface WriteOffReason {
  code: string;
  label: string;
  isDefault?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
}

const DEFAULT_REASONS: WriteOffReason[] = [
  { code: "CLIENT_DISCOUNT", label: "Client Discount", isDefault: true },
  { code: "GOODWILL", label: "Goodwill Gesture" },
  { code: "ERROR_CORRECTION", label: "Error / Correction" },
  { code: "BUDGET_OVERRUN", label: "Budget Overrun" },
  { code: "INTERNAL", label: "Internal — Not Billable" },
  { code: "AGREED_VALUE", label: "Agreed Value Adjustment" },
];

function loadWriteOffSettingsFallback(): { reasons: WriteOffReason[]; requireReason: boolean } {
  try {
    const storedReasons = localStorage.getItem("writeOffReasons");
    const storedRequire = localStorage.getItem("requireWriteOffReason");
    return {
      reasons: storedReasons ? JSON.parse(storedReasons) : DEFAULT_REASONS,
      requireReason: storedRequire === "true",
    };
  } catch {
    return { reasons: DEFAULT_REASONS, requireReason: false };
  }
}

async function fetchWriteOffSettings(): Promise<{ reasons: WriteOffReason[]; requireReason: boolean }> {
  try {
    const [reasonsRes, billingRes] = await Promise.all([
      fetch(`${getApiBaseUrl()}/settings/write-off-reasons`),
      fetch(`${getApiBaseUrl()}/settings/billing`),
    ]);
    const reasons: WriteOffReason[] = reasonsRes.ok ? await reasonsRes.json() : DEFAULT_REASONS;
    const billingSettings = billingRes.ok ? await billingRes.json() : { requireWriteOffReason: false };
    return {
      reasons: reasons.length > 0 ? reasons : DEFAULT_REASONS,
      requireReason: billingSettings.requireWriteOffReason === true,
    };
  } catch {
    return loadWriteOffSettingsFallback();
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

interface AgreedValueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedLines: AgreedValueLineItem[];
  onConfirm: (result: AgreedValueResult) => void;
}

export function AgreedValueDialog({
  open,
  onOpenChange,
  selectedLines,
  onConfirm,
}: AgreedValueDialogProps) {
  const [agreedValueRaw, setAgreedValueRaw] = useState("");
  const [agreedValueDisplay, setAgreedValueDisplay] = useState("");
  const [writeOffReason, setWriteOffReason] = useState("");
  const [{ reasons, requireReason }, setSettings] = useState<{
    reasons: WriteOffReason[];
    requireReason: boolean;
  }>({ reasons: [], requireReason: false });

  const loadSettings = useCallback(async () => {
    const settings = await fetchWriteOffSettings();
    setSettings(settings);
  }, []);

  useEffect(() => {
    if (open) {
      loadSettings();
      setAgreedValueRaw("");
      setAgreedValueDisplay("");
      setWriteOffReason("");
    }
  }, [open, loadSettings]);

  const actualTotal = useMemo(
    () => selectedLines.reduce((sum, l) => sum + l.amount, 0),
    [selectedLines]
  );

  const agreedNumeric = useMemo(() => {
    const raw = parseFloat(agreedValueRaw);
    return isNaN(raw) ? null : raw;
  }, [agreedValueRaw]);

  const writeOffAmount = useMemo(() => {
    if (agreedNumeric === null) return null;
    return actualTotal - agreedNumeric;
  }, [actualTotal, agreedNumeric]);

  const hasError = useMemo(() => {
    if (agreedNumeric === null) return false;
    if (agreedNumeric <= 0) return true;
    if (agreedNumeric > actualTotal) return true;
    return false;
  }, [agreedNumeric, actualTotal]);

  const errorMessage = useMemo(() => {
    if (agreedNumeric === null) return null;
    if (agreedNumeric <= 0) return "Agreed value must be greater than zero.";
    if (agreedNumeric > actualTotal)
      return `Agreed value cannot exceed the actual total of ${formatCurrency(actualTotal)}.`;
    return null;
  }, [agreedNumeric, actualTotal]);

  const canConfirm = useMemo(() => {
    if (agreedNumeric === null || hasError) return false;
    if (requireReason && !writeOffReason) return false;
    return true;
  }, [agreedNumeric, hasError, requireReason, writeOffReason]);

  // Format on blur
  const handleBlur = () => {
    if (agreedNumeric !== null && !hasError) {
      setAgreedValueDisplay(
        new Intl.NumberFormat("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(agreedNumeric)
      );
    }
  };

  // Strip formatting on focus
  const handleFocus = () => {
    setAgreedValueDisplay(agreedValueRaw);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits and one decimal point
    const cleaned = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    setAgreedValueRaw(cleaned);
    setAgreedValueDisplay(cleaned);
  };

  const handleConfirm = () => {
    if (!canConfirm || agreedNumeric === null || writeOffAmount === null) return;

    const scaleFactor = agreedNumeric / actualTotal;

    const result: AgreedValueResult = {
      lineIds: selectedLines.map((l) => l.id),
      agreedTotal: agreedNumeric,
      writeOffAmount,
      writeOffReason: writeOffReason || undefined,
      scaleFactor,
    };

    // Write-off WIP entries are created by the backend in PostToFinancialsAsync
    // when the invoice is successfully posted to Access Financials.
    onConfirm(result);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-muted-foreground" />
            Set Agreed Value
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Selected lines summary */}
          <div className="rounded-lg border bg-muted/30 divide-y">
            {selectedLines.map((line) => (
              <div
                key={line.id}
                className="flex items-center gap-3 px-3 py-2 text-sm min-w-0"
              >
                <span className="text-muted-foreground flex-1 min-w-0 truncate">
                  {line.description}
                </span>
                <span className="font-medium tabular-nums shrink-0 whitespace-nowrap">
                  {formatCurrency(line.amount)}
                </span>
              </div>
            ))}
          </div>

          {/* Actual total */}
          <div className="flex items-center justify-between gap-4 px-1">
            <span className="text-sm text-muted-foreground shrink-0">Actual total</span>
            <span className="text-sm font-semibold tabular-nums shrink-0">
              {formatCurrency(actualTotal)}
            </span>
          </div>

          <Separator />

          {/* Agreed value input */}
          <div className="space-y-2">
            <Label htmlFor="agreed-value">Agreed value</Label>
            <div
              className={cn(
                "flex items-center rounded-md border bg-background px-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                hasError && "border-destructive focus-within:ring-destructive"
              )}
            >
              <span className="text-sm text-muted-foreground mr-2 select-none">£</span>
              <input
                id="agreed-value"
                type="text"
                inputMode="decimal"
                className="flex-1 py-2 text-sm bg-transparent focus:outline-none"
                placeholder="0.00"
                value={agreedValueDisplay}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>
            {errorMessage && (
              <p className="flex items-center gap-1.5 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {errorMessage}
              </p>
            )}
          </div>

          {/* Write-off preview */}
          {writeOffAmount !== null && !hasError && writeOffAmount > 0 && (
            <div className="rounded-lg border border-warning-200 bg-warning-50 dark:border-warning-800 dark:bg-warning-950/30 px-4 py-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-warning-800 dark:text-warning-300">
                  Write-off
                </span>
                <span className="text-sm font-semibold tabular-nums text-warning-800 dark:text-warning-300">
                  {formatCurrency(writeOffAmount)}
                </span>
              </div>
              <p className="text-xs text-warning-700 dark:text-warning-400">
                The difference between the actual and agreed value will be
                recorded as a write-off against this invoice.
              </p>
            </div>
          )}

          {/* Write-off = 0 info */}
          {writeOffAmount !== null && !hasError && writeOffAmount === 0 && (
            <p className="text-xs text-muted-foreground text-center">
              The agreed value matches the actual total — no write-off will be recorded.
            </p>
          )}

          {/* Write-off reason */}
          {writeOffAmount !== null && !hasError && writeOffAmount > 0 && (
            <div className="space-y-2">
              <Label htmlFor="write-off-reason">
                Write-off reason
                {requireReason && (
                  <span className="text-destructive ml-1">*</span>
                )}
              </Label>
              <Select value={writeOffReason} onValueChange={setWriteOffReason}>
                <SelectTrigger id="write-off-reason">
                  <SelectValue placeholder="Select a reason…" />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <SelectItem key={r.code} value={r.code}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {requireReason && !writeOffReason && (
                <p className="text-xs text-muted-foreground">
                  A reason is required by your organisation's settings.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            Apply Agreed Value
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
